import {t} from './i18n.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';


// Keep short English sentence openings together without changing copied text.
export function keepSentenceOpenings(root, language, acrossInlineElements=true) {
  if(!/^en(?:-|$)/i.test(language || '')) return;
  const doc=root.ownerDocument;
  for(const block of root.querySelectorAll('p,li:not(:has(p))')) {
    if(!/^en(?:-|$)/i.test(block.closest('[lang]')?.lang || language)) continue;
    const walker=doc.createTreeWalker(block,4);
    const nodes=[];let offset=0,node;
    while((node=walker.nextNode())) {nodes.push({node,start:offset,end:offset+node.length});offset+=node.length;}
    const openings=/(^|[.!?…]\s+)["“‘«(]*(It|I|We|He|She|You)[ \t]+(am|is|are|was|were|do|does|did|can|could|will|would|have|has|had|may|might|should|must|won['’]t|can['’]t|(?:is|are|was|were|do|does|did|could|would|should|must)n['’]t)\b/g;
    for(const match of [...block.textContent.matchAll(openings)].reverse()) {
      const end=match.index+match[0].length;
      // Locate the subject rather than including sentence punctuation or quotes.
      const subjectStart=match.index+match[0].lastIndexOf(match[2]);
      const touched=nodes.filter(n=>n.end>subjectStart && n.start<end);
      if(!acrossInlineElements && touched.length>1) continue;
      if(!touched.length || touched.some(n=>n.node.parentElement.closest('a,code,pre,kbd,.sentence-opening'))) continue;
      const first=touched[0],last=touched[touched.length-1];
      const range=doc.createRange();range.setStart(first.node,subjectStart-first.start);range.setEnd(last.node,end-last.start);
      const span=doc.createElement('span');span.className='sentence-opening';span.append(range.extractContents());range.insertNode(span);
    }
  }
}
export function safeHtml(html, base, prepareImage) {
  const clean = DOMPurify.sanitize(html, { USE_PROFILES:{html:true,svg:true}, ADD_TAGS:['iframe'], FORBID_TAGS:['script','object','embed','form','input','button','style','link','meta'], FORBID_ATTR:['style','srcset'], RETURN_DOM_FRAGMENT:true });
  // Rebuild only recognized YouTube embeds with attributes controlled by the reader.
  for(const source of clean.querySelectorAll('iframe')) {
    let videoId;
    try {
      const url=new URL(source.getAttribute('src'));
      if(url.protocol==='https:'&&['www.youtube.com','www.youtube-nocookie.com'].includes(url.hostname)&&!url.port&&!url.username&&!url.password)videoId=url.pathname.match(/^\/embed\/([A-Za-z0-9_-]{11})$/)?.[1];
    } catch {}
    if(!videoId){source.remove();continue;}
    const frame=document.createElement('iframe');
    frame.src=`https://www.youtube-nocookie.com/embed/${videoId}`;
    frame.className='book-video'+(videoId==='ZktxO6adTnI'?' book-video-audiobook':'');frame.title=t("ui-youtube-video-player");frame.loading='lazy';
    frame.setAttribute('allow','accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    frame.setAttribute('referrerpolicy','strict-origin-when-cross-origin');frame.setAttribute('allowfullscreen','');
    const shell=document.createElement('div');
    shell.className='book-video-shell';shell.append(frame);
    source.replaceWith(shell);
  }
  for(const el of clean.querySelectorAll('[src],[href]')) {
    const attr = el.hasAttribute('src') ? 'src':'href';
    const val = el.getAttribute(attr);
    if(!val) continue;
    if(attr === 'href' && val.startsWith('#')) continue;
    try {
      const url = new URL(val,base || location.href);
      if(!['http:','https:','blob:','data:'].includes(url.protocol) || (url.protocol === 'data:' && !((attr === 'src' || (el.localName === 'image' && attr === 'href')) && /^data:image\//i.test(val)))) el.removeAttribute(attr);
      else if(base) el.setAttribute(attr,url.href);
      if(attr === 'href' && ['http:','https:'].includes(url.protocol) && url.origin !== location.origin) { el.setAttribute('target','_blank'); el.setAttribute('rel','noopener noreferrer'); }
    } catch { el.removeAttribute(attr); }
  }
  if(prepareImage)for(const image of clean.querySelectorAll('img'))prepareImage(image);
  const holder = document.createElement('div'); holder.append(clean); return holder.innerHTML;
}
export const slug = s => String(s).toLowerCase().normalize('NFC').replace(/[^\p{L}\p{N}\s-]/gu,'').trim().replace(/\s+/g,'-') || 'section';
export const escape = s => String(s).replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
export function stableBlocks(html, chapterId) {
  const holder = document.createElement('div'); holder.innerHTML = safeHtml(html);
  let n=0;
  for(const el of holder.querySelectorAll('p,li,blockquote,pre,h2,h3,h4,h5,h6,figure,aside')) {
    if(!el.id) el.id=`${chapterId}-b${++n}`;
  }
  return holder.innerHTML;
}
export function textDocument(source, {title=t("ui-untitled-reading"),markdown=true,id='text',language='en'}={}) {
  const html = markdown ? marked.parse(source,{gfm:true,breaks:false}) : source.split(/\n\s*\n/).filter(s=>s.trim()).map(s=>`<p>${escape(s).replace(/\n/g,'<br>')}</p>`).join('\n');
  const holder=document.createElement('div'); holder.innerHTML=safeHtml(html);
  const chapters=[]; let current={id:'introduction',title,html:''}; const ids=new Set();
  for(const node of [...holder.childNodes]) {
    if(node.nodeName === 'H1') {
      if(current.html.trim()) chapters.push(current);
      let key=slug(node.textContent); let n=2; const original=key; while(ids.has(key)) key=original+'-'+n++; ids.add(key);
      current={id:key,title:node.textContent,html:''};
    } else current.html += node.nodeType===3 ? escape(node.textContent) : node.outerHTML;
  }
  if(current.html.trim() || !chapters.length) chapters.push(current);
  for(const c of chapters){ c.html=stableBlocks(c.html,c.id); c.wordCount=htmlText(c.html).split(/\s+/).filter(Boolean).length; c.empty=!htmlText(c.html).trim(); }
  return {schemaVersion:1,id,title,author:'',kind:markdown?'markdown':'text',language,direction:'ltr',chapters,wordCount:chapters.reduce((sum,c)=>sum+c.wordCount,0),version:id,cover:''};
}
export function htmlText(html) { const el=document.createElement('div'); el.innerHTML=html; return el.textContent || ''; }
export const documentMetadata = b => ({id:b.id,title:b.title,author:b.author || '',kind:b.kind || 'pollen',language:b.language || 'en',cover:b.cover || '',coverWidth:b.coverWidth||0,coverHeight:b.coverHeight||0,coverSrcset:b.coverSrcset||'',chapterCount:b.chapterCount || b.chapters?.length || 0,wordCount:b.wordCount || 0,version:b.version || '',url:b.url || '',imported:!!b.imported});
